import React, { useState, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import { Eye, SquarePen, PauseCircle, PlayCircle, Trash2, MoreVertical } from 'lucide-react';
import { SettingsLayout } from '../../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  Dropdown,
  Input,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ConfirmDialog,
  Alert,
  CopyToClipboard,
} from '../../../../design-system/components';
import { formatDateTime } from '../../../../lib/formatters';
import {
  admin_settings_webhook_path,
  new_admin_settings_webhook_path,
  edit_admin_settings_webhook_path,
  toggle_active_admin_settings_webhook_path,
  admin_settings_inbound_webhook_sources_path,
  admin_settings_inbound_webhook_source_path,
} from '../../../../lib/routes';
function getActions(webhook, onToggle) {
  const items = [
    {
      label: 'View',
      onClick: () => router.visit(admin_settings_webhook_path(webhook.id)),
      icon: <Eye size={16} />,
    },
    {
      label: 'Edit',
      onClick: () => router.visit(edit_admin_settings_webhook_path(webhook.id)),
      icon: <SquarePen size={16} />,
    },
    {
      type: 'divider',
    },
    {
      label: webhook.active ? 'Deactivate' : 'Activate',
      onClick: () => onToggle(webhook),
      icon: webhook.active ? <PauseCircle size={16} /> : <PlayCircle size={16} />,
    },
    {
      label: 'Delete',
      onClick: () => {
        if (confirm('Are you sure you want to delete this webhook endpoint?')) {
          router.delete(admin_settings_webhook_path(webhook.id));
        }
      },
      destructive: true,
      icon: <Trash2 size={16} />,
    },
  ];
  return items;
}
function getColumns(onToggle) {
  return [
    {
      id: 'url',
      header: 'URL',
      sortable: true,
      render: (row) => (
        <Link
          href={admin_settings_webhook_path(row.id)}
          className="font-medium text-fg hover:text-fg-secondary"
        >
          <span className="font-mono text-sm">{row.url}</span>
        </Link>
      ),
    },
    {
      id: 'events',
      header: 'Events',
      render: (row) => {
        const events = Array.isArray(row.events) ? row.events : [];
        return (
          <span className="text-sm text-fg-secondary">
            {events.length === 0
              ? 'None'
              : `${events.length} event${events.length !== 1 ? 's' : ''}`}
          </span>
        );
      },
    },
    {
      id: 'event_count',
      header: 'Deliveries',
      sortable: true,
      render: (row) => row.event_count,
    },
    {
      id: 'active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.active ? 'success' : 'default'} size="sm">
          {row.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      sortable: true,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <Dropdown
          trigger={
            <button className="rounded p-1 text-fg-tertiary hover:bg-surface-muted hover:text-fg-secondary">
              <MoreVertical size={20} />
            </button>
          }
          items={getActions(row, onToggle)}
        />
      ),
    },
  ];
}

function getInboundColumns(onRevoke) {
  return [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => <span className="font-medium text-fg">{row.name}</span>,
    },
    {
      id: 'active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.active ? 'success' : 'default'} size="sm">
          {row.active ? 'Active' : 'Revoked'}
        </Badge>
      ),
    },
    {
      id: 'last_received_at',
      header: 'Last Received',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-fg-secondary">
          {row.last_received_at ? formatDateTime(row.last_received_at) : 'Never'}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      sortable: true,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <Button variant="ghost" size="sm" onClick={() => onRevoke(row)} className="text-danger-600 hover:text-danger-700">
          Revoke
        </Button>
      ),
    },
  ];
}

export default function Index({ webhooks, inbound_sources, raw_secret }) {
  // Outbound: optimistic toggle state
  const [optimisticToggles, setOptimisticToggles] = useState(new Set());
  const handleToggleActive = useCallback((webhook) => {
    setOptimisticToggles((prev) => new Set(prev).add(webhook.id));
    router.post(
      toggle_active_admin_settings_webhook_path(webhook.id),
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          setOptimisticToggles((prev) => {
            const next = new Set(prev);
            next.delete(webhook.id);
            return next;
          });
        },
      },
    );
  }, []);

  const displayWebhooks = webhooks.map((w) =>
    optimisticToggles.has(w.id)
      ? { ...w, active: !w.active }
      : w,
  );

  // Inbound: modal and confirm state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  function handleCreateSource() {
    setSubmitting(true);
    setErrors({});
    router.post(
      admin_settings_inbound_webhook_sources_path(),
      { inbound_webhook_source: { name } },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setName('');
          setSubmitting(false);
        },
        onError: (errs) => {
          setErrors(errs);
          setSubmitting(false);
        },
      },
    );
  }

  function handleRevokeSource(source) {
    router.delete(admin_settings_inbound_webhook_source_path(source.id), {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <SettingsLayout>
      <PageHeader
        title="Webhooks"
        description="Manage outbound webhook endpoints"
        actions={
          <Link href={new_admin_settings_webhook_path()}>
            <Button>New Endpoint</Button>
          </Link>
        }
      />

      <div className="mt-6">
        <DataTable
          columns={getColumns(handleToggleActive)}
          data={displayWebhooks}
          keyAccessor="id"
          emptyTitle="No webhook endpoints"
          emptyDescription="Create your first webhook endpoint to receive event notifications."
          emptyAction={
            <Link href={new_admin_settings_webhook_path()}>
              <Button>Create Endpoint</Button>
            </Link>
          }
        />
      </div>

      {/* Inbound Webhook Sources — super admins only */}
      {inbound_sources && (
        <>
          <div className="mt-12">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-fg">Inbound Webhook Sources</h2>
                <p className="text-sm text-fg-secondary">Manage inbound webhook sources for receiving external events</p>
              </div>
              <Button onClick={() => setShowCreateModal(true)}>Add Source</Button>
            </div>

            {raw_secret && (
              <div className="mb-4">
                <Alert variant="warning" title="New Webhook Source Created">
                  <p className="mb-2">Copy this secret now. It will not be shown again.</p>
                  <CopyToClipboard text={raw_secret}>
                    <code className="block rounded bg-surface-muted px-3 py-2 font-mono text-sm break-all">
                      {raw_secret}
                    </code>
                  </CopyToClipboard>
                </Alert>
              </div>
            )}

            <DataTable
              columns={getInboundColumns(setDeleteTarget)}
              data={inbound_sources}
              keyAccessor="id"
              emptyTitle="No inbound webhook sources"
              emptyDescription="Create a source to receive webhook events from external services."
              emptyAction={<Button onClick={() => setShowCreateModal(true)}>Add Source</Button>}
            />
          </div>

          {/* Create Source Modal */}
          <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} size="sm">
            <ModalTitle>Add Inbound Webhook Source</ModalTitle>
            <ModalBody>
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={errors.name}
                placeholder="e.g. Stripe Webhooks"
                helperText="A descriptive name for this webhook source"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateSource} loading={submitting} disabled={!name.trim()}>
                Create
              </Button>
            </ModalFooter>
          </Modal>

          {/* Revoke Confirmation */}
          <ConfirmDialog
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={() => deleteTarget && handleRevokeSource(deleteTarget)}
            title="Revoke Webhook Source"
            message={`Are you sure you want to revoke "${deleteTarget?.name}"? Webhooks signed with this secret will no longer be accepted.`}
            confirmLabel="Revoke"
            destructive
          />
        </>
      )}
    </SettingsLayout>
  );
}
